import React, { FC } from 'react';
import './StatusBar.css';
import LabelledNumber from '../labelled-number/LabelledNumber';
import LabelledPercent from '../labelled-number/LabelledPercent';
import LabelledInvalid from '../labelled-number/LabelledInvalid';
import Chart from '../highcharts/Chart';
import {
  Timestamp,
  ValueAtTheTime
} from '../../../../api/src/interfaces/shared';

export type StatTypes = 'return' | 'value';
export type Period = 'day' | 'week' | 'month' | 'year';

interface Props {
  currentStats: [Timestamp, ValueAtTheTime][];
  historicStats: [Timestamp, ValueAtTheTime][];
  labelType: StatTypes;
  period: Period;
  annualPercentageReturn?: number;
}

const periods: { [key: string]: [Timestamp, string] } = {
  day: [1000 * 60 * 60 * 36 * 1, 'dag'],
  week: [1000 * 60 * 60 * 24 * 8, 'uke'],
  month: [1000 * 60 * 60 * 24 * 31, 'mnd'],
  year: [1000 * 60 * 60 * 24 * 366, 'år']
};

export const StatusBar: FC<Props> = ({
  currentStats,
  historicStats,
  period,
  labelType,
  annualPercentageReturn
}) => {
  if (historicStats.length < 1) {
    return <div className="missing-stats"> Ikke nok data </div>;
  }

  const [currentTimestamp, currentValue] = currentStats[0];
  const allStats = [...historicStats, ...currentStats];

  const minimumTimestamp = currentTimestamp - periods[period][0];
  let firstHistoricStat = historicStats.find(
    stat => minimumTimestamp <= stat[0]
  );
  if (period === 'day' && !firstHistoricStat)
    firstHistoricStat = historicStats[historicStats.length - 1];
  if (!firstHistoricStat) firstHistoricStat = historicStats[0];

  const [, historicValue] = firstHistoricStat;

  const change = currentValue - historicValue;
  const changePercent = change / historicValue;
  const periodLabel = periods[period][1];
  const label = labelType === 'return' ? 'avkastning' : 'verdi';

  const filteredStats = allStats.filter(stat => stat[0] > minimumTimestamp);

  return (
    <div>
      <div className="status-wrapper">
        <div>
          <LabelledNumber number={currentValue} label={label} showPrefix={labelType === "return"} />
        </div>
        <div>
          <LabelledNumber
            number={change}
            showPrefix
            label={`siste ${periodLabel} (NOK)`}
          />
        </div>
        <div>
          {historicValue > 0 ? (
            <LabelledPercent
              number={changePercent}
              showPrefix
              label={`siste ${periodLabel} (%)`}
            />
          ) : (
            <LabelledInvalid label={`siste ${periodLabel} (%)`} />
          )}
        </div>
        {annualPercentageReturn && labelType === 'return' ? (
          <div>
            <LabelledPercent
              number={annualPercentageReturn}
              showPrefix
              label="Årlig avkastning"
            />
          </div>
        ) : (
          undefined
        )}
      </div>

      <Chart container="status-chart" chartKey="status" data={filteredStats} />
    </div>
  );
};
